"use client"

import React, { useEffect, useRef, useState } from "react"
import { EditorContent, EditorContext, useEditor } from "@tiptap/react"

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit"
import { ResizableImage } from "../../tiptap-node/image-node/resizable-image-extension"
import { TaskItem, TaskList } from "@tiptap/extension-list"
import { TextAlign } from "@tiptap/extension-text-align"
import { Typography } from "@tiptap/extension-typography"
import { Highlight } from "@tiptap/extension-highlight"
import { Subscript } from "@tiptap/extension-subscript"
import { Superscript } from "@tiptap/extension-superscript"
import { Selection } from "@tiptap/extensions"
import { TableKit } from "@tiptap/extension-table"

// --- UI Primitives ---
import { Button } from "../../tiptap-ui-primitive/button"
import { Spacer } from "../../tiptap-ui-primitive/spacer"
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "../../tiptap-ui-primitive/toolbar"

// --- Tiptap Node ---
import { ImageUploadNode } from "../../tiptap-node/image-upload-node/image-upload-node-extension"
import { HorizontalRule } from "../../tiptap-node/horizontal-rule-node/horizontal-rule-node-extension"
import "../../tiptap-node/blockquote-node/blockquote-node.scss"
import "../../tiptap-node/horizontal-rule-node/horizontal-rule-node.scss"
import "../../tiptap-node/list-node/list-node.scss"
import "../../tiptap-node/image-node/image-node.scss"
import "../../tiptap-node/heading-node/heading-node.scss"
import "../../tiptap-node/paragraph-node/paragraph-node.scss"

// --- Tiptap UI ---
import { HeadingDropdownMenu } from "../../tiptap-ui/heading-dropdown-menu"
import { ImageUploadButton } from "../../tiptap-ui/image-upload-button"
import { ListDropdownMenu } from "../../tiptap-ui/list-dropdown-menu"
import { BlockquoteButton } from "../../tiptap-ui/blockquote-button"
import { TableDropdownMenu } from "../../tiptap-ui/table-dropdown-menu"
import {
  ColorHighlightPopover,
  ColorHighlightPopoverContent,
  ColorHighlightPopoverButton,
} from "../../tiptap-ui/color-highlight-popover"
import {
  LinkPopover,
  LinkContent,
  LinkButton,
} from "../../tiptap-ui/link-popover"
import { MarkButton } from "../../tiptap-ui/mark-button"
import { TextAlignButton } from "../../tiptap-ui/text-align-button"
import { UndoRedoButton } from "../../tiptap-ui/undo-redo-button"

// --- Icons ---
import { ArrowLeftIcon } from "../../tiptap-icons/arrow-left-icon"
import { HighlighterIcon } from "../../tiptap-icons/highlighter-icon"
import { LinkIcon } from "../../tiptap-icons/link-icon"
import { AlignLeftIcon } from "../../tiptap-icons/align-left-icon"
import { AlignCenterIcon } from "../../tiptap-icons/align-center-icon"
import { AlignRightIcon } from "../../tiptap-icons/align-right-icon"

// --- Hooks ---
import { useIsMobile } from "../../../hooks/use-mobile"
import { useWindowSize } from "../../../hooks/use-window-size"
import { useCursorVisibility } from "../../../hooks/use-cursor-visibility"

// --- Components ---
// --- Lib ---
import { handleImageUpload, MAX_IMAGE_INPUT_SIZE } from "../../../lib/tiptap-utils"

// --- Styles ---
import "./simple-editor.scss"

// import content from "./data/content.json" // Now passed as prop

const MainToolbarContent = ({
  editor,
  onHighlighterClick,
  onLinkClick,
  isMobile
}) => {
  const isImageSelected = !!editor?.isActive("image");
  const activeImageAlign = isImageSelected
    ? (editor.getAttributes("image")?.align || "left")
    : null;

  const setImageAlign = (align) => {
    if (!editor || !isImageSelected) return;
    editor.chain().focus().updateAttributes("image", { align }).run();
  };

  return (
    <>
      <Spacer />
      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} portal={isMobile} />
        <ListDropdownMenu types={["bulletList", "orderedList", "taskList"]} portal={isMobile} />
        <BlockquoteButton />
        <TableDropdownMenu portal={isMobile} />
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="underline" />
        {!isMobile ? (
          <ColorHighlightPopover />
        ) : (
          <ColorHighlightPopoverButton onClick={onHighlighterClick} />
        )}
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <Button
          type="button"
          data-style="ghost"
          data-active-state={activeImageAlign === "left" ? "on" : "off"}
          disabled={!isImageSelected}
          aria-label="Align image left"
          tooltip={isImageSelected ? "Align image left" : "Select an image to align"}
          onClick={() => setImageAlign("left")}
        >
          <AlignLeftIcon className="tiptap-button-icon" />
        </Button>
        <Button
          type="button"
          data-style="ghost"
          data-active-state={activeImageAlign === "center" ? "on" : "off"}
          disabled={!isImageSelected}
          aria-label="Align image center"
          tooltip={isImageSelected ? "Align image center" : "Select an image to align"}
          onClick={() => setImageAlign("center")}
        >
          <AlignCenterIcon className="tiptap-button-icon" />
        </Button>
        <Button
          type="button"
          data-style="ghost"
          data-active-state={activeImageAlign === "right" ? "on" : "off"}
          disabled={!isImageSelected}
          aria-label="Align image right"
          tooltip={isImageSelected ? "Align image right" : "Select an image to align"}
          onClick={() => setImageAlign("right")}
        >
          <AlignRightIcon className="tiptap-button-icon" />
        </Button>
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <ImageUploadButton text="Add" />
      </ToolbarGroup>
      <Spacer />
    </>
  );
}

const MobileToolbarContent = ({
  type,
  onBack
}) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === "highlighter" ? (
      <ColorHighlightPopoverContent />
    ) : (
      <LinkContent />
    )}
  </>
)

export const SimpleEditor = React.forwardRef(({ content: contentProp = null, onBlur }, ref) => {
  const isMobile = useIsMobile()
  const { height } = useWindowSize()
  const [mobileView, setMobileView] = useState("main")
  const toolbarRef = useRef(null)
  const wrapperRef = useRef(null)

  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Main content area, start typing to enter text.",
        class: "simple-editor",
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        codeBlock: false,
        code: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      HorizontalRule,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      ResizableImage.configure({ allowBase64: true }),
      Typography,
      Superscript,
      Subscript,
      Selection,
      ImageUploadNode.configure({
        accept: "image/*",
        maxSize: MAX_IMAGE_INPUT_SIZE,
        limit: 3,
        upload: handleImageUpload,
        onError: (error) => console.error("Upload failed:", error),
      }),
      TableKit.configure({
        resizable: true,
      }),
    ],
    content: contentProp,
  })

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  })

  // Expose editor via ref
  React.useImperativeHandle(ref, () => ({
    editor,
  }), [editor]);

  useEffect(() => {
    if (!isMobile && mobileView !== "main") {
      setMobileView("main")
    }
  }, [isMobile, mobileView])

  // Handle blur event - detect when clicking outside the editor
  useEffect(() => {
    if (!onBlur || !editor) return

    const handleClickOutside = (event) => {
      if (!wrapperRef.current) return
      
      // Check if click is outside the editor wrapper (including toolbar)
      if (!wrapperRef.current.contains(event.target)) {
        // Small delay to ensure editor state is updated
        setTimeout(() => {
          if (onBlur) onBlur()
        }, 100)
      }
    }

    // Also listen for editor blur events
    const handleEditorBlur = () => {
      // Delay to check if focus moved to toolbar or another part of editor
      setTimeout(() => {
        if (!wrapperRef.current) return
        const activeElement = document.activeElement
        // If focus is not within the editor wrapper, trigger blur
        if (activeElement && !wrapperRef.current.contains(activeElement)) {
          if (onBlur) onBlur()
        }
      }, 100)
    }

    document.addEventListener('mousedown', handleClickOutside)
    if (editor.view?.dom) {
      editor.view.dom.addEventListener('blur', handleEditorBlur)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (editor.view?.dom) {
        editor.view.dom.removeEventListener('blur', handleEditorBlur)
      }
    }
  }, [editor, onBlur])

  return (
    <div className="simple-editor-wrapper" ref={wrapperRef}>
      <EditorContext.Provider value={{ editor }}>
        <Toolbar
          variant="fixed"
          ref={toolbarRef}
          style={{
            ...(isMobile
              ? {
                  bottom: `calc(100% - ${height - rect.y}px)`,
                }
              : {}),
          }}>
          {mobileView === "main" ? (
            <MainToolbarContent
              editor={editor}
              onHighlighterClick={() => setMobileView("highlighter")}
              onLinkClick={() => setMobileView("link")}
              isMobile={isMobile} />
          ) : (
            <MobileToolbarContent
              type={mobileView === "highlighter" ? "highlighter" : "link"}
              onBack={() => setMobileView("main")} />
          )}
        </Toolbar>

        <EditorContent editor={editor} role="presentation" className="simple-editor-content" />
      </EditorContext.Provider>
    </div>
  );
});

SimpleEditor.displayName = 'SimpleEditor';
